import { useEffect, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useLayoutShellStore } from '../../store/layoutShellStore';
import './LayoutShell.css';

/**
 * 공통 레이아웃 골격 셸(② ~ ⑤) — 설계 §4.
 * docs/design/design-20260717-common-layout-skeleton.md
 *
 * `AppLayout` 의 컨텐츠(③) 슬롯을 세분화하는 **additive** 셸이다(AppLayout 을 교체하지 않는다).
 * ① 좌측 레일(NavBar)·⑥ 하단 상태바는 AppLayout 이 소유하고, 본 셸은 그 안(③ 자리)에서
 * ②좌측상세 · ③컨텐츠 · ④문서패널 · ⑤우측레일을 배치한다.
 *
 * **슬롯 규약: 제공되지 않은(=undefined) 슬롯은 렌더하지 않는다(폭 0).** 따라서 어떤 페이지도
 * 본 셸을 쓰지 않으면(Phase 0) 시각 변화가 없다.
 *
 * 오버레이/인라인 도킹 상태는 `layoutShellStore` 가 소유. 라우트 전환 시 패널 축을 닫는다(§4.3).
 */
export interface LayoutShellProps {
  /** ② 좌측 상세 (2차 목록/트리). 미제공 시 미렌더. */
  secondary?: ReactNode;
  /** (가) 메뉴 설명 패널 — store.menuDescPanel 이 'closed' 가 아닐 때만. 미제공 시 미렌더. */
  menuDesc?: ReactNode;
  /** ③ 컨텐츠 (필수). */
  children: ReactNode;
  /** ④ 우측 상세 문서 패널 — store.docPanel 이 'closed' 가 아닐 때만. 미제공 시 미렌더. */
  docPanel?: ReactNode;
  /** ⑤ 우측 사이드 레일 — store.rightRail === 'shown' 일 때만. 미제공 시 미렌더. */
  rightRail?: ReactNode;
}

export function LayoutShell({ secondary, menuDesc, children, docPanel, rightRail }: LayoutShellProps) {
  const { pathname } = useLocation();
  const menuDescState = useLayoutShellStore((s) => s.menuDescPanel);
  const docPanelState = useLayoutShellStore((s) => s.docPanel);
  const rightRailState = useLayoutShellStore((s) => s.rightRail);
  const resetPanels = useLayoutShellStore((s) => s.resetPanelsForRouteChange);

  // 라우트 전환 시 패널 축 리셋 — 타 화면 진입 시 자동 열림 방지(§4.3).
  useEffect(() => {
    resetPanels();
  }, [pathname, resetPanels]);

  const showMenuDesc = menuDesc != null && menuDescState !== 'closed';
  const showDocPanel = docPanel != null && docPanelState !== 'closed';
  const showRightRail = rightRail != null && rightRailState === 'shown';

  return (
    <div className="layout-shell">
      {secondary != null && <div className="layout-shell__secondary">{secondary}</div>}

      <div className="layout-shell__center">
        {showMenuDesc && (
          <div
            className={`layout-shell__menudesc layout-shell__menudesc--${menuDescState}`}
            data-dock={menuDescState}
          >
            {menuDesc}
          </div>
        )}

        <div className="layout-shell__content scrollbar-thin">{children}</div>

        {showDocPanel && (
          <div
            className={`layout-shell__docpanel layout-shell__docpanel--${docPanelState}`}
            data-dock={docPanelState}
          >
            {docPanel}
          </div>
        )}
      </div>

      {showRightRail && <div className="layout-shell__rightrail">{rightRail}</div>}
    </div>
  );
}
